import { Conversation } from '@twilio/conversations'
import React, { useEffect, useState, FC } from 'react'
import { useSelector } from 'react-redux'

import { ConversationsList, ConversationThread, CreateConversation, ChatModal, ChatTrigger } from './components'
import { useTwilioConversations } from 'features/MerinioChat/hooks'

type Props = {
  chatToken: string | null
}

const MerinioChatWidget: FC<Props> = ({ chatToken }) => {
  const {
    isReady,
    conversations,
    createConversation,
    unreadMessagesTotal,
    conversationsUnreadMessagesMap,
    deleteConversation,
    conversationsLoading,
    setConversationsUnreadMessagesMap
  } = useTwilioConversations(chatToken)
  const [chatOpen, setChatOpen] = useState(false)
  const [activeConversation, setActiveConversation] = useState<Conversation | null>(null)
  const { users } = useSelector((state: MStore.RootState) => state.resources)
  const { userInfo: currentUser } = useSelector((state: MStore.RootState) => state.users)
  const [shouldHideList, setShouldHideList] = useState(false)

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setChatOpen(false)
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [setChatOpen])

  if (!isReady) return null

  return (
    <>
      <ChatModal chatOpen={chatOpen} setChatOpen={setChatOpen}>
        {activeConversation ? (
          <ConversationThread
            deleteConversation={deleteConversation}
            currentUser={currentUser}
            conversation={activeConversation}
            setActiveConversation={setActiveConversation}
            setConversationsUnreadMessagesMap={setConversationsUnreadMessagesMap}
          />
        ) : (
          <>
            <CreateConversation
              users={users}
              setShouldHideList={setShouldHideList}
              conversations={conversations}
              currentUser={currentUser}
              createConversation={createConversation}
              setActiveConversation={setActiveConversation}
            />
            {!shouldHideList ? (
              <ConversationsList
                conversationsLoading={conversationsLoading}
                conversationsUnreadMessagesMap={conversationsUnreadMessagesMap}
                conversations={conversations}
                setActiveConversation={setActiveConversation}
              />
            ) : null}
          </>
        )}
      </ChatModal>
      <ChatTrigger unreadMessagesTotal={unreadMessagesTotal} chatOpen={chatOpen} setChatOpen={setChatOpen} />
    </>
  )
}

function areEqual(prevProps: Props, nextProps: Props) {
  return prevProps.chatToken === nextProps.chatToken
}

const MerinioChat = React.memo(MerinioChatWidget, areEqual)

export { MerinioChat }
